import { useState, useEffect, useRef, useCallback, DragEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  api,
  SiteStatus,
  AdminWebsiteDetail as WebsiteDetailData,
  InfluencerSiteAsset,
  SiteAssetType,
} from '../api';

// ============================================================
// Config
// ============================================================

const STATUS_CONFIG: Record<SiteStatus, { label: string; color: string; bg: string }> = {
  form_incomplete:     { label: 'Form Incomplete', color: 'text-ink-400',    bg: 'bg-ink-700' },
  form_completed:      { label: 'Form Completed',  color: 'text-amber-400',  bg: 'bg-amber-500/20' },
  generating:          { label: 'Generating…',     color: 'text-violet-400', bg: 'bg-violet-500/20' },
  website_in_progress: { label: 'In Progress',     color: 'text-royal-400',  bg: 'bg-royal-500/20' },
  website_completed:   { label: 'Completed',       color: 'text-emerald-400',bg: 'bg-emerald-500/20' },
};

const ASSET_TYPE_LABELS: Record<SiteAssetType, string> = {
  logo:           'Logo',
  brand_photos:   'Brand Photos',
  product_photos: 'Product Photos',
  brand_kit:      'Brand Kit',
  other:          'Other',
};

// ============================================================
// Helpers
// ============================================================

function formatDate(dateStr: string | null) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function parseSocialLinks(json: string | null): Record<string, string> {
  if (!json) return {};
  try { return JSON.parse(json); } catch { return {}; }
}

function parseMustHavePages(json: string | null): string[] {
  if (!json) return [];
  try { return JSON.parse(json); } catch { return []; }
}

// ============================================================
// Sub-components
// ============================================================

interface StorefrontCredentials {
  shopify_storefront_api_token: string;
  shopify_storefront_id: string;
  private_storefront_api_token: string;
  public_customer_account_api_client_id: string;
  public_customer_account_api_url: string;
  shop_id: string;
}

interface GenerateDraftModalProps {
  stageName: string;
  onClose: () => void;
  onConfirm: (creds: StorefrontCredentials) => Promise<void>;
}

function GenerateDraftModal({ stageName, onClose, onConfirm }: GenerateDraftModalProps) {
  const [creds, setCreds] = useState<StorefrontCredentials>({
    shopify_storefront_api_token: '',
    shopify_storefront_id: '',
    private_storefront_api_token: '',
    public_customer_account_api_client_id: '',
    public_customer_account_api_url: '',
    shop_id: '',
  });
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState<{ count: number; preview: string } | null>(null);
  const [scanError, setScanError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const set = (k: keyof StorefrontCredentials) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setCreds((prev) => ({ ...prev, [k]: e.target.value }));

  // ── Screenshot scanning ──────────────────────────────────────

  const processImageFile = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setScanError('Please upload an image file (PNG, JPEG, or WebP).');
      return;
    }
    setScanning(true);
    setScanError('');
    setScanResult(null);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          // Strip the data:image/...;base64, prefix
          resolve(result.split(',')[1]);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const res = await api.parseShopifyScreenshot(base64, file.type);

      // Merge detected values — only overwrite empty fields
      setCreds((prev) => {
        const next = { ...prev };
        (Object.keys(res.credentials) as (keyof StorefrontCredentials)[]).forEach((k) => {
          if (res.credentials[k]) next[k] = res.credentials[k];
        });
        return next;
      });

      setScanResult({
        count: res.detected_count,
        preview: file.name,
      });
    } catch (err) {
      setScanError(err instanceof Error ? err.message : 'Failed to scan screenshot');
    } finally {
      setScanning(false);
    }
  };

  // Paste (Ctrl+V) anywhere in the modal
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of Array.from(items)) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) { processImageFile(file); break; }
        }
      }
    };
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, []);

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processImageFile(file);
  };

  // ── Form submit ──────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const missing = (Object.keys(creds) as (keyof StorefrontCredentials)[]).filter((k) => !creds[k].trim());
    if (missing.length > 0) {
      setError('All fields are required');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await onConfirm(creds);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start generation');
      setLoading(false);
    }
  };

  const filledCount = (Object.values(creds) as string[]).filter((v) => v.trim().length > 0).length;
  const totalFields = 6;

  const Field = ({
    label, hint, field, placeholder,
  }: { label: string; hint: string; field: keyof StorefrontCredentials; placeholder: string }) => (
    <div>
      <label className="block text-sm font-medium text-ink-300 mb-1 flex items-center gap-1.5">
        {label}
        <span className="text-ink-500 font-normal">{hint}</span>
        {creds[field] && (
          <span className="ml-auto text-emerald-500">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </span>
        )}
      </label>
      <input
        type="text"
        className={`input w-full font-mono text-sm transition-colors ${creds[field] ? 'border-emerald-500/30 bg-emerald-500/5' : ''}`}
        placeholder={placeholder}
        value={creds[field]}
        onChange={set(field)}
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div
        ref={modalRef}
        className="bg-ink-900 border border-ink-700 rounded-xl p-6 w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <h2 className="text-xl font-display font-bold text-ink-50 mb-1">Generate Website Draft</h2>
        <p className="text-sm text-ink-400 mb-4">
          Create a new Shopify Hydrogen storefront for{' '}
          <span className="text-ink-200">{stageName}</span>, then enter credentials below.
        </p>

        {/* Screenshot scan zone */}
        <div
          onClick={() => fileInputRef.current?.click()}
          className={`relative mb-5 rounded-lg border-2 border-dashed cursor-pointer transition-all select-none
            ${isDragging
              ? 'border-violet-500 bg-violet-500/10'
              : 'border-ink-600 hover:border-ink-500 hover:bg-ink-800/50'}`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) processImageFile(f); e.target.value = ''; }}
          />

          {scanning ? (
            <div className="flex items-center justify-center gap-3 py-5 px-4">
              <span className="w-5 h-5 border-2 border-violet-400/30 border-t-violet-400 rounded-full animate-spin flex-shrink-0" />
              <span className="text-sm text-violet-300">Scanning with Claude Vision…</span>
            </div>
          ) : scanResult ? (
            <div className="flex items-center gap-3 py-4 px-4">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-emerald-400">
                  Detected {scanResult.count}/{totalFields} fields
                </p>
                <p className="text-xs text-ink-500 truncate">{scanResult.preview} — click to scan a different screenshot</p>
              </div>
              <div className="text-xs text-ink-500 flex-shrink-0">
                {filledCount}/{totalFields} filled
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-2 py-5 px-4 text-center">
              <div className="w-9 h-9 rounded-lg bg-ink-700 flex items-center justify-center mb-1">
                <svg className="w-5 h-5 text-ink-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <p className="text-sm font-medium text-ink-300">Scan Shopify credentials screenshot</p>
              <p className="text-xs text-ink-500">
                Drop image here, click to browse, or{' '}
                <kbd className="px-1.5 py-0.5 rounded text-ink-400 bg-ink-700 font-mono text-xs border border-ink-600">⌘V</kbd>
                {' '}to paste — Claude Vision auto-fills all fields
              </p>
            </div>
          )}

          {isDragging && (
            <div className="absolute inset-0 rounded-lg bg-violet-500/10 flex items-center justify-center pointer-events-none">
              <p className="text-violet-300 font-medium text-sm">Drop to scan</p>
            </div>
          )}
        </div>

        {scanError && (
          <p className="text-xs text-accent-400 bg-accent-500/10 border border-accent-500/20 rounded-lg px-3 py-2 mb-4">
            {scanError}
          </p>
        )}

        {/* Manual fields */}
        <form onSubmit={handleSubmit} className="space-y-3">
          <Field
            label="Public Storefront API Token"
            hint="(shpat_...)"
            field="shopify_storefront_api_token"
            placeholder="shpat_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
          />
          <Field
            label="Storefront ID"
            hint="(numeric ID)"
            field="shopify_storefront_id"
            placeholder="1000110601"
          />
          <Field
            label="Private Storefront API Token"
            hint="(server-side)"
            field="private_storefront_api_token"
            placeholder="shpat_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
          />
          <Field
            label="Customer Account API Client ID"
            hint="(UUID)"
            field="public_customer_account_api_client_id"
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
          />
          <Field
            label="Customer Account API URL"
            hint="(shopify.com/...)"
            field="public_customer_account_api_url"
            placeholder="https://shopify.com/12345678"
          />
          <Field
            label="Shop ID"
            hint="(numeric)"
            field="shop_id"
            placeholder="12345678"
          />

          {error && (
            <p className="text-sm text-accent-400 bg-accent-500/10 border border-accent-500/20 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} disabled={loading} className="btn-ghost flex-1">
              Cancel
            </button>
            <button type="submit" disabled={loading || scanning} className="btn-primary flex-1">
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Starting…
                </span>
              ) : `Generate Draft${filledCount === totalFields ? ' →' : ` (${filledCount}/${totalFields})`}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface BuildStatusCardProps {
  influencerDbId: number;
  stagingUrl: string | null;
  onOpenEditor: () => void;
}

function BuildStatusCard({ influencerDbId, stagingUrl, onOpenEditor }: BuildStatusCardProps) {
  const [buildStatus, setBuildStatus] = useState<{
    ready: boolean;
    status: string;
    conclusion: string | null;
    run_url: string | null;
  } | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState('');
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopBuildPoll = () => {
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
  };

  const pollBuild = useCallback(async () => {
    try {
      const res = await api.getBuildStatus(influencerDbId);
      setBuildStatus(res);
      return res;
    } catch {
      return null;
    }
  }, [influencerDbId]);

  const startBuildPoll = useCallback(() => {
    stopBuildPoll();
    intervalRef.current = setInterval(async () => {
      const res = await api.getBuildStatus(influencerDbId).catch(() => null);
      if (res) {
        setBuildStatus(res);
        const done = res.conclusion === 'success' || res.conclusion === 'failure' || res.conclusion === 'cancelled';
        if (done) stopBuildPoll();
      }
    }, 10000);
  }, [influencerDbId]);

  useEffect(() => {
    pollBuild();
    startBuildPoll();
    return () => stopBuildPoll();
  }, [influencerDbId, pollBuild, startBuildPoll]);

  const handleRetryBuild = async () => {
    setRetrying(true);
    setRetryError('');
    try {
      await api.retryBuild(influencerDbId);
      // Reset build status so spinner shows again, then resume polling
      setBuildStatus(null);
      startBuildPoll();
    } catch (e) {
      setRetryError(e instanceof Error ? e.message : 'Failed to retry build');
    } finally {
      setRetrying(false);
    }
  };

  const isBuilding = !buildStatus || buildStatus.status === 'queued' || buildStatus.status === 'in_progress';
  const isFailed = buildStatus?.conclusion === 'failure' || buildStatus?.conclusion === 'cancelled';

  return (
    <div className="card p-6">
      <h2 className="text-lg font-display font-bold text-ink-100 mb-4">GitHub Actions Build</h2>

      <div className="flex items-center gap-3 mb-4">
        {isBuilding && (
          <>
            <span className="w-4 h-4 border-2 border-violet-400/30 border-t-violet-400 rounded-full animate-spin flex-shrink-0" />
            <span className="text-sm text-violet-300">
              {buildStatus?.status === 'queued'
                ? 'Build queued…'
                : buildStatus?.status === 'in_progress'
                  ? 'Building and deploying…'
                  : 'Waiting for build to start…'}
            </span>
          </>
        )}
        {buildStatus?.conclusion === 'success' && (
          <>
            <span className="w-4 h-4 bg-emerald-400 rounded-full flex-shrink-0" />
            <span className="text-sm text-emerald-400 font-medium">Deployed successfully</span>
          </>
        )}
        {isFailed && (
          <>
            <span className="w-4 h-4 bg-accent-400 rounded-full flex-shrink-0" />
            <span className="text-sm text-accent-400 font-medium">
              Build {buildStatus?.conclusion} — click "Retry Build" to try again
            </span>
          </>
        )}
      </div>

      {retryError && (
        <p className="text-xs text-accent-400 mb-3">{retryError}</p>
      )}

      <div className="flex flex-wrap gap-3">
        {buildStatus?.run_url && (
          <a
            href={buildStatus.run_url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost text-sm flex items-center gap-1.5"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
            View Build Logs
          </a>
        )}

        {isFailed && (
          <button
            onClick={handleRetryBuild}
            disabled={retrying}
            className="btn-ghost text-sm flex items-center gap-1.5 text-amber-400 border-amber-500/30 hover:bg-amber-500/10"
          >
            {retrying ? (
              <span className="w-3.5 h-3.5 border-2 border-amber-400/30 border-t-amber-400 rounded-full animate-spin" />
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            )}
            {retrying ? 'Retrying…' : 'Retry Build'}
          </button>
        )}

        {stagingUrl && (
          <a
            href={stagingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost text-sm flex items-center gap-1.5"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
            {stagingUrl.replace('https://', '')}
          </a>
        )}

        {buildStatus?.conclusion === 'success' && (
          <button onClick={onOpenEditor} className="btn-primary text-sm flex items-center gap-1.5">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
            </svg>
            Open Editor
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Main component
// ============================================================

export default function AdminWebsiteDetail() {
  const { influencerDbId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [data, setData] = useState<WebsiteDetailData | null>(null);
  const [status, setStatus] = useState<SiteStatus>('form_incomplete');
  const [siteDomain, setSiteDomain] = useState('');
  const [assets, setAssets] = useState<InfluencerSiteAsset[]>([]);

  // Generate draft modal
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Upload state
  const [uploadingType, setUploadingType] = useState<SiteAssetType | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedAssetType, setSelectedAssetType] = useState<SiteAssetType>('logo');

  // Polling for generating → website_in_progress transition
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const loadData = useCallback(async () => {
    try {
      const detail = await api.getWebsiteDetail(parseInt(influencerDbId!));
      setData(detail);
      setStatus(detail.site.status);
      setSiteDomain(detail.site.site_domain || '');
      setAssets(detail.assets);
      return detail.site.status;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load website details');
    } finally {
      setLoading(false);
    }
  }, [influencerDbId]);

  useEffect(() => {
    if (!influencerDbId) return;
    loadData().then((s) => {
      if (s === 'generating') startPolling();
    });
    return () => stopPolling();
  }, [influencerDbId, loadData]);

  const startPolling = () => {
    stopPolling();
    pollingRef.current = setInterval(async () => {
      try {
        const detail = await api.getWebsiteDetail(parseInt(influencerDbId!));
        setData(detail);
        setStatus(detail.site.status);
        if (detail.site.status !== 'generating') {
          stopPolling();
          // Show generation error if failed
          if (detail.site.generation_error) {
            setError(`Generation failed: ${detail.site.generation_error}`);
          }
        }
      } catch {
        // ignore transient errors during polling
      }
    }, 5000);
  };

  const handleGenerateDraft = async (creds: StorefrontCredentials, force = false) => {
    setShowGenerateModal(false);
    setError('');
    setStatus('generating');
    setData((prev) => prev
      ? { ...prev, site: { ...prev.site, status: 'generating', generation_error: null } }
      : prev);

    // Start polling BEFORE the POST — the backend runs synchronously (30-120s)
    // and writes step progress to the DB, which polling picks up every 5s.
    startPolling();

    try {
      await api.generateDraft(parseInt(influencerDbId!), { ...creds, force });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Generation request failed';
      setError(msg);
      // Reload data to get the latest DB state
      loadData();
    }
  };

  const handleForceReset = async () => {
    if (!confirm('Reset the generating status back to "Form Completed"? Do this only if generation is stuck.')) return;
    setResetting(true);
    try {
      await api.resetGeneration(parseInt(influencerDbId!));
      stopPolling();
      await loadData();
      setSuccess('Status reset. You can now try generating again.');
      setTimeout(() => setSuccess(''), 4000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reset generation status');
    } finally {
      setResetting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.updateWebsite(parseInt(influencerDbId!), {
        status,
        site_domain: siteDomain.trim() || null,
      });
      setSuccess('Website updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update website');
    } finally {
      setSaving(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !data) return;
    setUploadingType(selectedAssetType);
    setUploadProgress(0);
    try {
      const presign = await api.presignSiteAsset({
        assetType: selectedAssetType,
        fileName: file.name,
        contentType: file.type,
        sizeBytes: file.size,
        influencer_id: data.influencer.id,
      });
      setUploadProgress(30);
      await api.uploadSiteAsset(presign.uploadUrl, file);
      setUploadProgress(70);
      const result = await api.completeSiteAssetUpload({
        influencer_id: data.influencer.id,
        assetType: selectedAssetType,
        fileName: file.name,
        contentType: file.type,
        sizeBytes: file.size,
        objectKey: presign.objectKey,
        publicUrl: presign.publicUrl,
      });
      setAssets([result.asset, ...assets]);
      setUploadProgress(100);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload file');
    } finally {
      setUploadingType(null);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteAsset = async (assetId: number) => {
    if (!confirm('Are you sure you want to delete this asset?')) return;
    try {
      await api.deleteSiteAsset(assetId);
      setAssets(assets.filter((a) => a.id !== assetId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete asset');
    }
  };

  // ── Render ──────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-accent-500/30 border-t-accent-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-ink-400">Website not found</p>
        <button onClick={() => navigate('/admin/websites')} className="btn-ghost mt-4">
          Back to Websites
        </button>
      </div>
    );
  }

  const socialLinks = parseSocialLinks(data.intake.social_links);
  const mustHavePages = parseMustHavePages(data.intake.must_have_pages);
  const isGenerating = status === 'generating';
  const hasRepo = !!data.site.github_repo_name;
  const generationError = data.site.generation_error;
  const canGenerate = (status === 'form_completed' || status === 'form_incomplete') && !!data.intake.submitted_at;
  const canRegenerate = (status === 'website_in_progress' || status === 'website_completed') && !!data.intake.submitted_at;

  return (
    <>
      {showGenerateModal && (
        <GenerateDraftModal
          stageName={data.influencer.stage_name}
          onClose={() => setShowGenerateModal(false)}
          onConfirm={(creds) => handleGenerateDraft(creds, isGenerating)}
        />
      )}

      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/websites')} className="btn-ghost p-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-3xl font-display font-bold text-ink-50">{data.influencer.stage_name}</h1>
              <p className="text-ink-400 mt-1">
                {data.influencer.first_name} {data.influencer.last_name} • {data.influencer.email || 'No email'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Generate / Regenerate / Force-generate Draft button */}
            {(canGenerate || canRegenerate || isGenerating) && (
              <button
                onClick={() => setShowGenerateModal(true)}
                className={`flex items-center gap-2 ${isGenerating ? 'btn-ghost border-amber-500/30 text-amber-400 hover:bg-amber-500/10' : 'btn-primary'}`}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                {isGenerating ? 'Force Regenerate' : canRegenerate ? 'Regenerate Draft' : 'Generate Draft'}
              </button>
            )}

            {/* Open Editor — available whenever repo exists */}
            {hasRepo && !isGenerating && (
              <button
                onClick={() => navigate(`/admin/websites/${influencerDbId}/editor`)}
                className="btn-ghost flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
                Open Editor
              </button>
            )}

            <button onClick={handleSave} disabled={saving || isGenerating} className="btn-secondary">
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-accent-500/10 border border-accent-500/20 rounded-lg p-4 text-accent-400">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4 text-emerald-400">
            {success}
          </div>
        )}

        {/* Generation error from DB (pipeline failed, status reverted) */}
        {!isGenerating && generationError && (
          <div className="bg-accent-500/10 border border-accent-500/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-accent-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1 min-w-0">
                <p className="text-accent-300 font-medium">Last generation failed</p>
                <p className="text-accent-400/80 text-sm mt-1 font-mono break-all">{generationError}</p>
              </div>
            </div>
          </div>
        )}

        {/* Generating banner */}
        {isGenerating && (
          <div className="bg-violet-500/10 border border-violet-500/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <span className="w-5 h-5 border-2 border-violet-400/30 border-t-violet-400 rounded-full animate-spin flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-violet-300 font-medium">Generating website draft…</p>
                {/* Show live step from DB — generation_error is reused as a progress field during generation */}
                {data.site.generation_error ? (
                  <p className="text-violet-400/80 text-sm mt-0.5 font-mono">{data.site.generation_error}</p>
                ) : (
                  <p className="text-violet-400/70 text-sm mt-0.5">
                    Starting pipeline… This page auto-refreshes every 5 seconds.
                  </p>
                )}
              </div>
              <button
                onClick={handleForceReset}
                disabled={resetting}
                title="Force-reset if generation is stuck"
                className="text-xs text-violet-400/50 hover:text-violet-400 transition-colors flex-shrink-0 ml-2 whitespace-nowrap"
              >
                {resetting ? 'Resetting…' : 'Force reset'}
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">

            {/* Build Status Card — shown once repo is created */}
            {hasRepo && (
              <BuildStatusCard
                influencerDbId={parseInt(influencerDbId!)}
                stagingUrl={data.site.staging_url}
                onOpenEditor={() => navigate(`/admin/websites/${influencerDbId}/editor`)}
              />
            )}

            {/* Status & Domain */}
            <div className="card p-6">
              <h2 className="text-lg font-display font-bold text-ink-100 mb-4">Website Status</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-ink-300 mb-1">Status</label>
                  {isGenerating ? (
                    <div className={`px-3 py-2 rounded text-sm font-medium inline-flex items-center gap-2 ${STATUS_CONFIG.generating.bg} ${STATUS_CONFIG.generating.color}`}>
                      <span className="w-2 h-2 rounded-full bg-violet-400 animate-pulse" />
                      {STATUS_CONFIG.generating.label}
                    </div>
                  ) : (
                    <select
                      value={status}
                      onChange={(e) => setStatus(e.target.value as SiteStatus)}
                      className="input w-full"
                    >
                      {(Object.keys(STATUS_CONFIG) as SiteStatus[]).filter(s => s !== 'generating').map((s) => (
                        <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink-300 mb-1">Site Domain</label>
                  <input
                    type="url"
                    className="input w-full"
                    value={siteDomain}
                    onChange={(e) => setSiteDomain(e.target.value)}
                    placeholder="https://shop.example.com"
                    disabled={isGenerating}
                  />
                </div>
              </div>

              {/* GitHub / Worker info */}
              {hasRepo && (
                <div className="mt-4 pt-4 border-t border-ink-800 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-ink-500 text-xs uppercase tracking-wide mb-1">GitHub Repo</p>
                    <a href={data.site.github_repo_url!} target="_blank" rel="noopener noreferrer"
                      className="text-accent-400 hover:text-accent-300 truncate block">
                      {data.site.github_repo_name}
                    </a>
                  </div>
                  <div>
                    <p className="text-ink-500 text-xs uppercase tracking-wide mb-1">Worker Name</p>
                    <span className="font-mono text-ink-300">{data.site.worker_name}</span>
                  </div>
                </div>
              )}

              <p className="text-xs text-ink-500 mt-3">Last updated: {formatDate(data.site.updated_at)}</p>
            </div>

            {/* Intake Data */}
            <div className="card p-6">
              <h2 className="text-lg font-display font-bold text-ink-100 mb-4">
                Intake Form
                {data.intake.submitted_at && (
                  <span className="text-sm font-normal text-ink-400 ml-2">
                    Submitted {formatDate(data.intake.submitted_at)}
                  </span>
                )}
              </h2>

              {!data.intake.submitted_at ? (
                <p className="text-ink-400">Intake form has not been submitted yet.</p>
              ) : (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-sm font-semibold text-ink-200 mb-2">Brand & Vibe</h3>
                    <dl className="space-y-3">
                      <div>
                        <dt className="text-xs text-ink-500 uppercase">Brand Vibe</dt>
                        <dd className="text-ink-200">{data.intake.brand_vibe || '-'}</dd>
                      </div>
                      <div>
                        <dt className="text-xs text-ink-500 uppercase">Color Scheme</dt>
                        <dd className="text-ink-200">{data.intake.color_scheme || '-'}</dd>
                      </div>
                      <div>
                        <dt className="text-xs text-ink-500 uppercase">Font Preferences</dt>
                        <dd className="text-ink-200">{data.intake.font_preferences || '-'}</dd>
                      </div>
                    </dl>
                  </div>

                  {Object.keys(socialLinks).length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-ink-200 mb-2">Social Links</h3>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(socialLinks).filter(([, v]) => v).map(([platform, url]) => (
                          <a key={platform} href={url} target="_blank" rel="noopener noreferrer"
                            className="px-3 py-1 bg-ink-800 rounded text-sm text-accent-400 hover:bg-ink-700">
                            {platform}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {data.intake.inspiration_urls && (
                    <div>
                      <h3 className="text-sm font-semibold text-ink-200 mb-2">Inspiration URLs</h3>
                      <div className="space-y-1">
                        {data.intake.inspiration_urls.split(',').filter(Boolean).map((url, idx) => (
                          <a key={idx} href={url.trim()} target="_blank" rel="noopener noreferrer"
                            className="block text-sm text-accent-400 hover:text-accent-300 truncate">
                            {url.trim()}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <h3 className="text-sm font-semibold text-ink-200 mb-2">Content & Messaging</h3>
                    <dl className="space-y-3">
                      <div>
                        <dt className="text-xs text-ink-500 uppercase">About / Bio</dt>
                        <dd className="text-ink-200 whitespace-pre-wrap">{data.intake.about_copy || '-'}</dd>
                      </div>
                      <div>
                        <dt className="text-xs text-ink-500 uppercase">Callouts / Highlights</dt>
                        <dd className="text-ink-200">{data.intake.callouts || '-'}</dd>
                      </div>
                    </dl>
                  </div>

                  {mustHavePages.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-ink-200 mb-2">Must-Have Pages</h3>
                      <div className="flex flex-wrap gap-2">
                        {mustHavePages.map((page) => (
                          <span key={page} className="px-2 py-1 bg-ink-800 rounded text-sm text-ink-300 capitalize">
                            {page}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {data.intake.notes && (
                    <div>
                      <h3 className="text-sm font-semibold text-ink-200 mb-2">Additional Notes</h3>
                      <p className="text-ink-200 whitespace-pre-wrap">{data.intake.notes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Assets */}
            <div className="card p-6">
              <h2 className="text-lg font-display font-bold text-ink-100 mb-4">Uploaded Assets</h2>

              <div className="flex gap-2 mb-4">
                <select
                  className="input flex-1 text-sm"
                  value={selectedAssetType}
                  onChange={(e) => setSelectedAssetType(e.target.value as SiteAssetType)}
                >
                  {Object.entries(ASSET_TYPE_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.pdf,.zip"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!!uploadingType}
                  className="btn-ghost text-sm whitespace-nowrap"
                >
                  {uploadingType ? `${uploadProgress}%` : 'Upload'}
                </button>
              </div>

              {assets.length === 0 ? (
                <p className="text-sm text-ink-500">No assets uploaded yet.</p>
              ) : (
                <div className="space-y-2">
                  {assets.map((asset) => (
                    <div key={asset.id} className="flex items-center gap-3 p-2 bg-ink-800/50 rounded">
                      <div className="w-12 h-12 bg-ink-700 rounded flex items-center justify-center overflow-hidden flex-shrink-0">
                        {asset.content_type.startsWith('image/') ? (
                          <a href={asset.public_url} target="_blank" rel="noopener noreferrer">
                            <img src={asset.public_url} alt="" className="w-full h-full object-cover" />
                          </a>
                        ) : (
                          <a href={asset.public_url} target="_blank" rel="noopener noreferrer" className="text-ink-400 hover:text-accent-400">
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                          </a>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <a href={asset.public_url} target="_blank" rel="noopener noreferrer"
                          className="text-sm text-ink-200 hover:text-accent-400 truncate block">
                          {asset.file_name}
                        </a>
                        <p className="text-xs text-ink-500">
                          {ASSET_TYPE_LABELS[asset.asset_type]} • {formatFileSize(asset.size_bytes)}
                        </p>
                      </div>
                      <button
                        onClick={() => handleDeleteAsset(asset.id)}
                        className="btn-ghost p-1 text-ink-400 hover:text-accent-400 flex-shrink-0"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quick Info */}
            <div className="card p-6">
              <h2 className="text-lg font-display font-bold text-ink-100 mb-4">Quick Info</h2>
              <dl className="space-y-3 text-sm">
                <div>
                  <dt className="text-ink-500">Influencer ID</dt>
                  <dd className="text-ink-200 font-mono">{data.influencer.influencer_id}</dd>
                </div>
                <div>
                  <dt className="text-ink-500">Storefront ID</dt>
                  <dd className="text-ink-200">{data.influencer.storefront_id}</dd>
                </div>
                {data.site.staging_url && (
                  <div>
                    <dt className="text-ink-500">Staging URL</dt>
                    <dd>
                      <a href={data.site.staging_url} target="_blank" rel="noopener noreferrer"
                        className="text-accent-400 hover:text-accent-300 truncate block font-mono text-xs">
                        {data.site.staging_url}
                      </a>
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="text-ink-500">Site Created</dt>
                  <dd className="text-ink-200">{formatDate(data.site.created_at)}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
